import { useMemo, useState } from 'react';
import { Box, Collapse, Flex, Paper, Table, Text } from '@mantine/core';
import { IconFilter } from '@tabler/icons-react';
import { JourneyIconButton, SectionHeader } from './shared';
import { SortableTh, sortRows, useTableSort } from './sorting';

// column_key is the `<campaignId>_<goalOrd>` prefix the backend uses on metric keys.
export type AdvancedFilterGoalMeta = {
  goal_ord: string;
  display_name: string;
  type: string;
  column_key: string;
  goal_key?: string;
};

export type AdvancedFilterRow = Record<string, any>;

export type AdvancedFilterTablesData = {
  eligible: boolean;
  goals: AdvancedFilterGoalMeta[];
  source: AdvancedFilterRow[];
  tracking_tag: AdvancedFilterRow[];
  custom_tag: Record<string, AdvancedFilterRow[]>;
};

type Metric = { suffix: string; label: string };

// ALL_METRICS labels → backend column suffixes. Unmapped labels render nothing.
const GOAL_METRIC_BY_LABEL: Record<string, Metric> = {
  Conversions: { suffix: '_visiopt_conversions', label: 'Conversions' },
  Revenue:     { suffix: '_visiopt_revenue',     label: 'Revenue'     },
  UCR:         { suffix: '_ucr',                 label: 'UCR'         },
  CR:          { suffix: '_cr',                  label: 'CR'          },
  AOV:         { suffix: '_aov',                 label: 'AOV'         },
  EPC:         { suffix: '_epc',                 label: 'EPC'         },
};

// One goal cell as shown on screen and in the CSV: $ on money, % on rates, "-" for AOV without conversions.
export const formatAdvancedCell = (value: any, suffix: string, conversions: number): string => {
  if (value === null || value === undefined || value === '') { return '-'; }
  if (suffix === '_visiopt_conversions') { return String(parseInt(String(value), 10) || 0); }
  if (suffix === '_aov' && conversions <= 0) { return '-'; }
  const num = Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(num)) { return String(value); }
  if (suffix === '_visiopt_revenue' || suffix === '_aov' || suffix === '_epc') {
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (suffix === '_ucr' || suffix === '_cr') { return `${num.toFixed(2)}%`; }
  return String(value);
};

// Unions identifier keys across all rows so an empty first row never hides a column.
const sniffIdentifierKeys = (rows: AdvancedFilterRow[], goalPrefixes: string[]): string[] => {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const row of rows) {
    for (const k of Object.keys(row)) {
      if (seen.has(k)) { continue; }
      if (k === 'uniques' || k === 'visits') { continue; }
      if (k === 'source_id') { continue; }
      if (k.endsWith('_id')) { continue; }
      if (goalPrefixes.some((p) => k.startsWith(p + '_'))) { continue; }
      seen.add(k);
      ordered.push(k);
    }
  }
  return ordered;
};

const HEADER_LABEL_OVERRIDES: Record<string, string> = {
  'Source name': 'Source Name',
  'Utm Content': 'Utm Content',
  'Utm Term': 'Utm Term',
  'Utm Campaign name': 'Utm Campaign Name',
  'Utm Medium': 'Utm Medium',
  'Utm Source name': 'Utm Source Name',
  'Utm Id': 'Utm Id',
};
const headerLabel = (key: string): string => HEADER_LABEL_OVERRIDES[key] ?? key;

const identifierValue = (raw: any): string => {
  if (raw === null || raw === undefined) { return '-'; }
  const s = String(raw);
  return s === '' ? '-' : s;
};

const TableBlock = ({
  rows,
  goals,
  metrics,
  emptyMessage,
  onConversionClick,
}: {
  rows: AdvancedFilterRow[];
  goals: AdvancedFilterGoalMeta[];
  metrics: string[];
  emptyMessage: string;
  onConversionClick?: (row: AdvancedFilterRow, goal: AdvancedFilterGoalMeta) => void;
}) => {
  const { sort, toggle } = useTableSort();
  const sortedRows = useMemo(
    () => sortRows(rows, sort, (row, field) => row[field]),
    [rows, sort],
  );
  if (rows.length === 0) {
    return <Text size="sm" color="dimmed" py="xs">{emptyMessage}</Text>;
  }
  const goalPrefixes = goals.map((g) => g.column_key);
  const idKeys = sniffIdentifierKeys(rows, goalPrefixes);
  const showUniques = metrics.includes('Uniques');
  const showVisits  = metrics.includes('Visits');
  const activeGoalMetrics: Metric[] = metrics
    .map((label) => GOAL_METRIC_BY_LABEL[label])
    .filter((m): m is Metric => m !== undefined);

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table withBorder withColumnBorders striped highlightOnHover fontSize="xs">
        <thead>
          <tr>
            {idKeys.map((k) => (
              <SortableTh key={'id_'+k} field={k} sort={sort} onToggle={toggle} thStyle={{ whiteSpace: 'nowrap' }}>
                {headerLabel(k)}
              </SortableTh>
            ))}
            {showUniques && (
              <SortableTh field="uniques" sort={sort} onToggle={toggle} align="right" thStyle={{ whiteSpace: 'nowrap' }}>
                Uniques
              </SortableTh>
            )}
            {showVisits && (
              <SortableTh field="visits" sort={sort} onToggle={toggle} align="right" thStyle={{ whiteSpace: 'nowrap' }}>
                Visits
              </SortableTh>
            )}
            {goals.flatMap((goal) =>
              activeGoalMetrics.map((m) => (
                <SortableTh
                  key={goal.column_key + m.suffix}
                  field={goal.column_key + m.suffix}
                  sort={sort}
                  onToggle={toggle}
                  align="right"
                  thStyle={{ whiteSpace: 'nowrap' }}
                >
                  {`${goal.display_name} — ${m.label}`}
                </SortableTh>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, ri) => (
            <tr key={ri}>
              {idKeys.map((k) => (
                <td key={'idv_'+k} style={{ whiteSpace: 'nowrap' }}>{identifierValue(row[k])}</td>
              ))}
              {showUniques && <td style={{ whiteSpace: 'nowrap' }}>{Number(row.uniques ?? 0)}</td>}
              {showVisits  && <td style={{ whiteSpace: 'nowrap' }}>{Number(row.visits  ?? 0)}</td>}
              {goals.flatMap((goal) =>
                activeGoalMetrics.map((m) => {
                  const key = goal.column_key + m.suffix;
                  const raw = row[key];
                  const display = formatAdvancedCell(raw, m.suffix, parseInt(String(row[goal.column_key + '_visiopt_conversions'] ?? 0), 10) || 0);
                  const convCount = m.suffix === '_visiopt_conversions' ? parseInt(String(raw ?? 0), 10) || 0 : 0;
                  const clickable =
                    m.suffix === '_visiopt_conversions' &&
                    !!onConversionClick &&
                    !!goal.goal_key &&
                    convCount > 0;
                  if (clickable) {
                    return (
                      <td key={key}>
                        <Flex align="center" gap={6}>
                          {display}
                          <JourneyIconButton onClick={() => onConversionClick!(row, goal)} />
                        </Flex>
                      </td>
                    );
                  }
                  return <td key={key}>{display}</td>;
                })
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </Box>
  );
};

// Tracking Tag row column label → backend utm key. Only populated columns flow into the
// tracking_tag_data JSON blob (matches the row aggregation in tracking_tag_data_pg.php).
const TRACKING_LABEL_TO_UTM: Record<string, string> = {
  'Utm Id':             'utm_id',
  'Utm Source name':    'utm_source',
  'Utm Medium':         'utm_medium',
  'Utm Campaign name':  'utm_campaign',
  'Utm Term':           'utm_term',
  'Utm Content':        'utm_content',
};

const collectTrackingTagBlob = (row: AdvancedFilterRow): { blob: string; label: string } => {
  const fields: Record<string, string> = {};
  const labelParts: string[] = [];
  for (const [columnLabel, utmKey] of Object.entries(TRACKING_LABEL_TO_UTM)) {
    const raw = row[columnLabel];
    if (raw === null || raw === undefined || raw === '') { continue; }
    const value = String(raw);
    fields[utmKey] = value;
    labelParts.push(`${columnLabel}: ${value}`);
  }
  if (Object.keys(fields).length === 0) { return { blob: '', label: '' }; }
  // btoa expects latin1; encodeURIComponent → unescape ensures unicode utm values survive.
  const blob = btoa(unescape(encodeURIComponent(JSON.stringify(fields))));
  return { blob, label: labelParts.join(' · ') };
};

export const AdvancedFilterTables = ({
  data,
  metrics,
  isDarkTheme: _isDarkTheme,
  onSourceJourneyOpen,
  onTrackingJourneyOpen,
  onCustomJourneyOpen,
}: {
  data: AdvancedFilterTablesData;
  metrics: string[];
  isDarkTheme?: boolean;
  onSourceJourneyOpen?: (args: { sourceId: string; sourceName: string; goalKey: string; goalLabel: string }) => void;
  onTrackingJourneyOpen?: (args: { trackingTagData: string; entityLabel: string; goalKey: string; goalLabel: string }) => void;
  onCustomJourneyOpen?: (args: { tagId: string; tagValue: string; tagName: string; goalKey: string; goalLabel: string }) => void;
}) => {
  const [sourceOpen, setSourceOpen] = useState(true);
  const [trackingOpen, setTrackingOpen] = useState(true);
  const [customOpen, setCustomOpen] = useState(true);

  if (!data.eligible) { return null; }

  const hasSource   = data.source.length > 0;
  const hasTracking = data.tracking_tag.length > 0;
  const customKeys  = Object.keys(data.custom_tag || {});
  const hasCustom   = customKeys.length > 0;

  if (!hasSource && !hasTracking && !hasCustom) { return null; }

  const paperSx = (theme: any) => ({
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
  });

  return (
    <>
      {hasSource && (
        <Box mt="lg">
          <SectionHeader
            icon={<IconFilter size="1rem" color="#a855f7" />}
            label="Traffic Source"
            opened={sourceOpen}
            toggle={() => setSourceOpen((o) => !o)}
          />
          <Collapse in={sourceOpen}>
            <Paper withBorder radius="md" p="sm" mt="sm" sx={paperSx}>
              <TableBlock
                rows={data.source}
                goals={data.goals}
                metrics={metrics}
                emptyMessage="No source data."
                onConversionClick={
                  onSourceJourneyOpen
                    ? (row, goal) => {
                        const sourceId = String(row.source_id ?? '');
                        if (!sourceId || !goal.goal_key) { return; }
                        const sourceName = String(row['Source name'] ?? '');
                        onSourceJourneyOpen({
                          sourceId,
                          sourceName,
                          goalKey: goal.goal_key,
                          goalLabel: goal.display_name,
                        });
                      }
                    : undefined
                }
              />
            </Paper>
          </Collapse>
        </Box>
      )}
      {hasTracking && (
        <Box mt="lg">
          <SectionHeader
            icon={<IconFilter size="1rem" color="#0ea5e9" />}
            label="Tracking Tag"
            opened={trackingOpen}
            toggle={() => setTrackingOpen((o) => !o)}
          />
          <Collapse in={trackingOpen}>
            <Paper withBorder radius="md" p="sm" mt="sm" sx={paperSx}>
              <TableBlock
                rows={data.tracking_tag}
                goals={data.goals}
                metrics={metrics}
                emptyMessage="No tracking-tag data."
                onConversionClick={
                  onTrackingJourneyOpen
                    ? (row, goal) => {
                        if (!goal.goal_key) { return; }
                        const { blob, label } = collectTrackingTagBlob(row);
                        if (blob === '') { return; }
                        onTrackingJourneyOpen({
                          trackingTagData: blob,
                          entityLabel: label,
                          goalKey: goal.goal_key,
                          goalLabel: goal.display_name,
                        });
                      }
                    : undefined
                }
              />
            </Paper>
          </Collapse>
        </Box>
      )}
      {hasCustom && (
        <Box mt="lg">
          <SectionHeader
            icon={<IconFilter size="1rem" color="#f59e0b" />}
            label="Custom Tag"
            opened={customOpen}
            toggle={() => setCustomOpen((o) => !o)}
          />
          <Collapse in={customOpen}>
            <Paper withBorder radius="md" p="sm" mt="sm" sx={paperSx}>
              {customKeys.map((tagKey) => {
                const rows = Array.isArray(data.custom_tag[tagKey]) ? data.custom_tag[tagKey] : [];
                return (
                  <Box key={tagKey} mb="md">
                    <Text size="sm" weight={600} mb={4}>{tagKey}</Text>
                    <TableBlock
                      rows={rows}
                      goals={data.goals}
                      metrics={metrics}
                      emptyMessage="No data."
                      onConversionClick={
                        onCustomJourneyOpen
                          ? (row, goal) => {
                              if (!goal.goal_key) { return; }
                              const tagId = String(row[`${tagKey}_id`] ?? '');
                              const tagValue = String(row[tagKey] ?? '');
                              if (!tagId || !tagValue) { return; }
                              onCustomJourneyOpen({
                                tagId,
                                tagValue,
                                tagName: tagKey,
                                goalKey: goal.goal_key,
                                goalLabel: goal.display_name,
                              });
                            }
                          : undefined
                      }
                    />
                  </Box>
                );
              })}
            </Paper>
          </Collapse>
        </Box>
      )}
    </>
  );
};
